import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ScopedPage } from '@/layouts/ScopedPage';
import { hasCapability, useProjectScope } from '@/context/ProjectContext';
import { useListMembersQuery, useSearchAuditLogQuery } from '@/services/api';
import { redact } from '@/utils/redaction';
import { formatTime, formatTimestamp } from '@/utils/format';
import { AGENT_ROLES } from '@/utils/agentConfig';
import type { AuditLogEntryDto, AuditLogQuery } from '@/types/api';


export const EVENT_TYPES = [
  'workflow.started',
  'workflow.paused_for_approval',
  'workflow.completed',
  'stage.started',
  'stage.completed',
  'stage.paused_for_approval',
  'stage.failed',
  'approval.decided',
  'escalation_decision',
  'approval.sla_breached',
  'pipeline_failure',
  'workspace.provision_started',
  'workspace.provisioned',
  'workspace.provision_failed',
  'git_output.pushed',
  'git_output.pr_opened',
  'git_output.pr_failed',
  'token_usage'
] as const;

const PAGE_SIZES = [10, 25, 50, 100] as const;

const SYSTEM_ACTORS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'workflow-engine', label: 'Workflow engine' },
  ...AGENT_ROLES.map((r) => ({ value: r.role, label: r.label })),
  { value: 'doubt-analyst', label: 'Doubt analyst' }
];

interface FilterState {
  event_type: string;
  emitted_from: string;
  emitted_to: string;
  actor: string;
  target_resource: string;
}

export function last24h(): { from: string; to: string } {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const fmt = (d: Date): string => d.toISOString().slice(0, 16);
  return { from: fmt(yesterday), to: fmt(now) };
}

export function utcInputToIso(value: string): string {
  return new Date(`${value}:00Z`).toISOString();
}

export function AuditLogPage(): JSX.Element {
  const { project, user } = useProjectScope();

  if (!hasCapability(user, 'audit_log:read')) {
    return (
      <ScopedPage title="Audit log">
        <h1 className="page-title">Audit log</h1>
        <div className="card">
          <div className="empty" style={{ padding: '80px 24px' }}>
            <div className="empty-icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0A0A0A"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={0.65}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M5 5 l14 14" />
              </svg>
            </div>
            <p className="empty-title">You don&apos;t have access to the audit log</p>
            <p className="empty-sub">
              The audit log is restricted to project administrators. Contact a project admin to request
              access.
            </p>
            <div className="mono-cap" style={{ marginBottom: 16 }}>
              capability required · audit_log:read
            </div>
            <div>
              <Link className="btn" to={`/p/${project.slug}/dashboard`}>
                ← Back to project
              </Link>
            </div>
          </div>
        </div>
      </ScopedPage>
    );
  }

  return (
    <ScopedPage title="Audit log">
      <h1 className="page-title">Audit log</h1>
      <AuditLogTable projectId={project.project_id} />
    </ScopedPage>
  );
}

function AuditLogTable({ projectId }: { projectId: string }): JSX.Element {
  const initialRange = last24h();
  const [draft, setDraft] = useState<FilterState>({
    event_type: '',
    emitted_from: initialRange.from,
    emitted_to: initialRange.to,
    actor: '',
    target_resource: ''
  });
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [cursorStack, setCursorStack] = useState<Array<{ cursor: string | undefined; offset: number }>>(
    [{ cursor: undefined, offset: 0 }]
  );
  const [applied, setApplied] = useState<FilterState>(draft);

  const top = cursorStack[cursorStack.length - 1]!;
  const cursor = top.cursor;
  const pageStart = top.offset;
  const query: AuditLogQuery = {
    project_id: projectId,
    page_size: pageSize,
    ...(applied.event_type ? { event_type: applied.event_type } : {}),
    ...(applied.emitted_from ? { emitted_from: utcInputToIso(applied.emitted_from) } : {}),
    ...(applied.emitted_to ? { emitted_to: utcInputToIso(applied.emitted_to) } : {}),
    ...(applied.actor.trim() ? { actor: applied.actor.trim() } : {}),
    ...(applied.target_resource.trim() ? { target_resource: applied.target_resource.trim() } : {}),
    ...(cursor ? { cursor } : {})
  };

  const { data, isLoading, isFetching, isError, refetch } = useSearchAuditLogQuery(query);

  const { data: memberData } = useListMembersQuery(projectId);
  const actorOptions = [
    ...SYSTEM_ACTORS,
    ...(memberData?.members ?? []).map((m) => ({
      value: m.user_id,
      label: m.email ?? m.name ?? m.user_id
    }))
  ];

  function apply(): void {
    setCursorStack([{ cursor: undefined, offset: 0 }]);
    setApplied(draft);
  }
  function reset(): void {
    const r = last24h();
    const cleared: FilterState = {
      event_type: '',
      emitted_from: r.from,
      emitted_to: r.to,
      actor: '',
      target_resource: ''
    };
    setDraft(cleared);
    setApplied(cleared);
    setCursorStack([{ cursor: undefined, offset: 0 }]);
  }

  const events = data?.events ?? [];
  const onFirstPage = cursorStack.length === 1;

  return (
    <div className="card">
      <div className="filters">
        <div className="filter-field">
          <label className="field-label" htmlFor="al-event-type">
            Event type
          </label>
          <select
            id="al-event-type"
            className="select"
            value={draft.event_type}
            onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}
          >
            <option value="">All event types</option>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <label className="field-label">Time range (UTC)</label>
          <div className="filter-range">
            <input
              className="input"
              type="datetime-local"
              aria-label="From"
              value={draft.emitted_from}
              onChange={(e) => setDraft({ ...draft, emitted_from: e.target.value })}
            />
            <span className="filter-range-sep">→</span>
            <input
              className="input"
              type="datetime-local"
              aria-label="To"
              value={draft.emitted_to}
              onChange={(e) => setDraft({ ...draft, emitted_to: e.target.value })}
            />
          </div>
        </div>
        <div className="filter-field">
          <label className="field-label" htmlFor="al-principal">
            Actor
          </label>
          <select
            id="al-principal"
            className="select"
            value={draft.actor}
            onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
          >
            <option value="">All actors</option>
            {actorOptions.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <label className="field-label" htmlFor="al-target">
            Target resource
          </label>
          <input
            id="al-target"
            className="input"
            type="text"
            autoComplete="off-al-target"
            placeholder="pipeline_run_id / project_id / …"
            value={draft.target_resource}
            onChange={(e) => setDraft({ ...draft, target_resource: e.target.value })}
          />
        </div>
        <div className="filter-actions">
          <button className="btn primary sm" onClick={apply}>
            Apply
          </button>
          <button className="btn ghost sm" onClick={reset}>
            Reset
          </button>
        </div>
      </div>

      {isLoading || isFetching ? (
        <AuditLogSkeleton rows={pageSize} />
      ) : isError ? (
        <div
          className="error-row"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 18,
            alignItems: 'center',
            margin: 22,
            padding: '14px 18px',
            borderLeft: '3px solid var(--orange)',
            background: 'rgba(255,166,82,0.08)',
            borderRadius: '0 var(--radius-sm) var(--radius-sm) 0'
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>Could not load events</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              The request failed — please try again.
            </div>
          </div>
          <button className="btn primary sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="empty">
          <p className="empty-title">No events match these filters</p>
          <p className="empty-sub">
            Try widening the time range, clearing the actor filter, or selecting &quot;All event types&quot;.
          </p>
          <button className="btn" onClick={reset}>
            Reset filters
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
        <table className="audit-table" style={{ minWidth: 1180, tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 130 }}>Timestamp</th>
              <th style={{ width: 190 }}>Event type</th>
              <th style={{ width: 140 }}>Actor</th>
              <th style={{ width: 80 }}>Role</th>
              <th style={{ width: 160 }}>Message</th>
              <th style={{ width: 200 }}>Target resource</th>
              <th>Payload summary</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <AuditRow key={ev.event_id} entry={ev} />
            ))}
          </tbody>
        </table>
        </div>
      )}

      <div className="pagination">
        <span className="pager-info">
          {isFetching
            ? 'loading…'
            : typeof data?.total === 'number'
              ?
                `${pageStart + 1}–${pageStart + events.length} of ${data.total.toLocaleString()}`
              : `${events.length} results${data?.next_cursor ? ' · more available' : ''}`}
        </span>
        <div className="pager-controls" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className="mono-cap">Per page</span>
          <select
            className="select sm-sel"
            style={{ width: 80 }}
            aria-label="Page size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCursorStack([{ cursor: undefined, offset: 0 }]);
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            className="btn ghost sm"
            disabled={isFetching || onFirstPage}
            onClick={() => setCursorStack((s) => s.slice(0, -1))}
          >
            ← Prev
          </button>
          <button
            className="btn ghost sm"
            disabled={isFetching || !data?.next_cursor}
            onClick={() =>
              setCursorStack((s) => {
                const next = data?.next_cursor ?? undefined;
                if (next === undefined || s[s.length - 1]?.cursor === next) return s;
                return [
                  ...s,
                  {
                    cursor: next,
                    offset: (s[s.length - 1]?.offset ?? 0) + events.length
                  }
                ];
              })
            }
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntryDto }): JSX.Element {
  const safePayload = redact(entry.payload_summary);
  return (
    <tr>
      <td>
        <span style={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatTime(entry.emitted_at)}
        </span>
        <span className="ts">{formatTimestamp(entry.emitted_at).slice(0, 10)}</span>
      </td>
      <td>
        <span className="event-type">{entry.event_type}</span>
      </td>
      <td>
        {entry.actor_email ? (
          <>
            <span style={{ display: 'block', fontSize: 13 }}>{entry.actor_email}</span>
            <span className="ts mono">{entry.actor}</span>
          </>
        ) : (
          <span className="mono" style={{ fontSize: 13 }}>
            {entry.actor}
          </span>
        )}
      </td>
      <td>
        {entry.actor_role ? (
          <span className={`role-chip ${entry.actor_role === 'admin' ? 'admin' : ''}`}>
            {entry.actor_role}
          </span>
        ) : (
          <span style={{ color: 'var(--text-subtle)' }}>—</span>
        )}
      </td>
      <td>
        <span className="target" title={entry.message ?? undefined}>
          {entry.message ?? <span style={{ color: 'var(--text-subtle)' }}>—</span>}
        </span>
      </td>
      <td>
        <span className="target">{entry.target_resource}</span>
      </td>
      <td className="payload">
        <div className="payload-clamp">
          <PayloadSummary payload={safePayload} />
        </div>
      </td>
    </tr>
  );
}

function PayloadSummary({ payload }: { payload: Record<string, unknown> }): JSX.Element {
  const entries = Object.entries(payload);
  if (entries.length === 0) return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
  return (
    <>
      {entries.map(([k, v], i) => {
        const isRedacted = v === '[REDACTED]';
        return (
          <span key={k}>
            {i > 0 && ' · '}
            {isRedacted ? (
              <span className="redacted" data-testid={`redacted-${k}`}>
                {k}
              </span>
            ) : (
              <>
                {k}=<code>{String(v)}</code>
              </>
            )}
          </span>
        );
      })}
    </>
  );
}

function AuditLogSkeleton({ rows }: { rows: number }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table
        className="audit-table"
        aria-busy="true"
        style={{ minWidth: 1180, tableLayout: 'fixed', width: '100%' }}
      >
        <thead>
          <tr>
            <th style={{ width: 130 }}>Timestamp</th>
            <th style={{ width: 190 }}>Event type</th>
            <th style={{ width: 140 }}>Actor</th>
            <th style={{ width: 80 }}>Role</th>
            <th style={{ width: 160 }}>Message</th>
            <th style={{ width: 200 }}>Target resource</th>
            <th>Payload summary</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              {Array.from({ length: 7 }, (_, j) => (
                <td key={j} className={j === 6 ? 'payload' : undefined}>
                  <div className={j === 6 ? 'payload-clamp' : undefined}>
                    <div
                      className="animate-skel"
                      style={{
                        height: 12,
                        borderRadius: 3,
                        background: 'var(--surface-sunken)',
                        width: `${50 + ((i + j) % 4) * 12}%`
                      }}
                    />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
